import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@pactfront/ui";

import { AnalyticsConsentControl } from "./AnalyticsConsentControl.js";
import { preloadMatchScreen } from "./matchScreenLoader.js";
import { commands } from "./network.js";
import { useClientStore } from "./store.js";

type PlayPath = "training" | "solo" | "multiplayer";

const playPaths: readonly PlayPath[] = ["training", "solo", "multiplayer"];

function PlayPathIcon({ path }: { path: PlayPath }) {
  if (path === "training") {
    return (
      <svg viewBox="0 0 48 48" aria-hidden="true">
        <circle cx="24" cy="24" r="13" />
        <path d="M24 4v10M24 34v10M4 24h10M34 24h10" />
        <circle cx="24" cy="24" r="3" />
      </svg>
    );
  }
  if (path === "solo") {
    return (
      <svg viewBox="0 0 48 48" aria-hidden="true">
        <rect x="7" y="8" width="34" height="25" rx="2" />
        <path d="M18 41h12M24 33v8" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true">
      <circle cx="24" cy="15" r="7" />
      <circle cx="9" cy="20" r="5" />
      <circle cx="39" cy="20" r="5" />
      <path d="M12 42v-5c0-7 5-12 12-12s12 5 12 12v5M1 42v-4c0-5 3-9 8-10M47 42v-4c0-5-3-9-8-10" />
    </svg>
  );
}

export function Landing() {
  const { t } = useTranslation();
  const connected = useClientStore((state) => state.connected);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [pending, setPending] = useState(false);
  const [selectedPath, setSelectedPath] = useState<PlayPath>("multiplayer");
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const privacyTitleId = useId();

  useEffect(() => {
    if (!privacyOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPrivacyOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [privacyOpen]);

  const run = async (action: () => Promise<unknown>) => {
    setPending(true);
    try {
      await action();
    } finally {
      setPending(false);
    }
  };

  const choosePath = (path: PlayPath) => {
    setSelectedPath(path);
    if (path !== "training") preloadMatchScreen();
  };

  const canCreate = connected && !pending && name.trim().length >= 2;
  const canJoin = canCreate && code.length === 6;

  return (
    <main className="landing-page">
      <header className="landing-header">
        <div className="wordmark">{t("app.brand")}</div>
        <a href="#how-it-works">{t("landing.howItWorks")}</a>
      </header>

      <section className="landing-hero" aria-labelledby="landing-title">
        <div className="landing-hero-copy">
          <h1 id="landing-title" aria-label={t("landing.title")}>
            <span aria-hidden="true">{t("landing.titleLineOne")}</span>
            <span aria-hidden="true">{t("landing.titleLineTwo")}</span>
          </h1>
          <p className="landing-lead">{t("landing.description")}</p>
          <p className="landing-duration">{t("landing.duration")}</p>
          <div className="landing-hero-actions">
            <a className="landing-primary-link" href="#choose-command">
              {t("landing.chooseHow")}
              <span aria-hidden="true">→</span>
            </a>
            <a href="#how-it-works">{t("landing.seeReveal")}</a>
          </div>
        </div>

        <figure className="landing-game-preview">
          <img
            src="/assets/pactfront-command-preview.png"
            width="1280"
            height="720"
            alt={t("landing.previewAlt")}
            fetchPriority="high"
          />
          <figcaption className="sr-only">{t("landing.previewCaption")}</figcaption>
        </figure>
      </section>

      <section className="landing-command" id="choose-command" aria-labelledby="command-title">
        <h2 id="command-title">{t("landing.command.title")}</h2>
        <div className="landing-command-layout">
          <div className="play-path-rail" role="group" aria-label={t("landing.command.title")}>
            {playPaths.map((path) => (
              <button
                key={path}
                type="button"
                data-testid={`play-path-${path}`}
                className={selectedPath === path ? "is-selected" : undefined}
                aria-pressed={selectedPath === path}
                onFocus={path === "training" ? undefined : preloadMatchScreen}
                onMouseEnter={path === "training" ? undefined : preloadMatchScreen}
                onClick={() => choosePath(path)}
              >
                <span className="play-path-icon">
                  <PlayPathIcon path={path} />
                </span>
                <span className="play-path-copy">
                  <strong>{t(`landing.command.${path}.title`)}</strong>
                  <span>{t(`landing.command.${path}.description`)}</span>
                </span>
                <span className="play-path-action">{t(`landing.command.${path}.action`)}</span>
              </button>
            ))}
          </div>

          <form className="landing-entry-form" onSubmit={(event) => event.preventDefault()}>
            <label>
              <span>{t("landing.name")}</span>
              <input
                data-testid="guest-name"
                value={name}
                maxLength={24}
                autoComplete="nickname"
                placeholder={t("landing.namePlaceholder")}
                onChange={(event) => setName(event.target.value)}
              />
            </label>

            {selectedPath === "training" ? (
              <div className="training-availability" role="status">
                <p>{t("landing.trainingUnavailable")}</p>
                <Button type="button" disabled data-testid="start-training">
                  {t("landing.command.training.action")}
                </Button>
                <button type="button" onClick={() => choosePath("solo")}>
                  {t("landing.practiceSolo")}
                </button>
              </div>
            ) : selectedPath === "solo" ? (
              <Button
                type="submit"
                data-testid="start-solo-match"
                tone="primary"
                disabled={!canCreate}
                onFocus={preloadMatchScreen}
                onMouseEnter={preloadMatchScreen}
                onClick={() => void run(() => commands.createLobby(name.trim()))}
              >
                {t("landing.command.solo.action")}
              </Button>
            ) : (
              <>
                <label>
                  <span>{t("landing.codeOptional")}</span>
                  <input
                    data-testid="lobby-code-input"
                    value={code}
                    maxLength={6}
                    autoCapitalize="characters"
                    autoComplete="off"
                    placeholder={t("landing.codePlaceholder")}
                    onChange={(event) => setCode(event.target.value.toUpperCase())}
                  />
                </label>
                <div className="landing-room-actions">
                  <Button
                    type="submit"
                    data-testid="join-lobby"
                    disabled={!canJoin}
                    onFocus={preloadMatchScreen}
                    onMouseEnter={preloadMatchScreen}
                    onClick={() => void run(() => commands.joinLobby(name.trim(), code))}
                  >
                    {t("landing.join")}
                  </Button>
                  <Button
                    type="submit"
                    data-testid="create-lobby"
                    tone="primary"
                    disabled={!canCreate}
                    onFocus={preloadMatchScreen}
                    onMouseEnter={preloadMatchScreen}
                    onClick={() => void run(() => commands.createLobby(name.trim()))}
                  >
                    {t("landing.create")}
                  </Button>
                </div>
              </>
            )}
          </form>
        </div>
      </section>

      <section className="landing-reveal" id="how-it-works" aria-labelledby="reveal-title">
        <h2 id="reveal-title">{t("landing.reveal.title")}</h2>
        <ol>
          {["plan", "ready", "reveal"].map((step, index) => (
            <li key={step}>
              <span>{index + 1}</span>
              <div>
                <strong>{t(`landing.reveal.${step}.title`)}</strong>
                <p>{t(`landing.reveal.${step}.description`)}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <footer className="landing-trust">
        <h2>{t("landing.fairPlay.title")}</h2>
        <p>{t("landing.fairPlay.description")}</p>
        <button type="button" data-testid="privacy-choices" onClick={() => setPrivacyOpen(true)}>
          {t("landing.privacyChoices")}
        </button>
      </footer>

      {privacyOpen ? (
        <div
          className="privacy-dialog-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setPrivacyOpen(false);
          }}
        >
          <section
            className="landing-privacy-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={privacyTitleId}
          >
            <header>
              <h2 id={privacyTitleId}>{t("landing.privacyChoices")}</h2>
              <button
                type="button"
                autoFocus
                data-testid="privacy-close"
                aria-label={t("landing.closePrivacy")}
                onClick={() => setPrivacyOpen(false)}
              >
                ×
              </button>
            </header>
            <AnalyticsConsentControl />
          </section>
        </div>
      ) : null}
    </main>
  );
}
